// ===============================================================
// File Library — Categories & Tags Management
// CRUD for document categories with color and icon settings
// Standard tags management stored in KV config
// ===============================================================

import { useEffect, useState, useCallback } from 'react';
import {
  Title, Text, Paper, Badge, Button, Group, TextInput, Stack, Divider,
  NumberInput, Select, Modal, Center, Loader, ActionIcon, Tabs,
} from '../../components/gab/GabComponents';
import { useDisclosure } from '../../components/gab/GabHooks';
import { notifications } from '../../lib/notifications';
import { IconPlus, IconPencil, IconTrash, IconTag, IconCategory } from '@tabler/icons-react';
import { useFileLibraryStore } from '../../store/fileLibraryStore';
import type { FLCategory } from '../../store/fileLibraryStore';
import { WORKER_URL } from '../../lib/api-switch';

const API = WORKER_URL;
const H = { 'Content-Type': 'application/json', 'Origin': 'https://apps.opengov.com' };

export default function Categories() {
  const {
    categories, documents, loading,
    loadCategories, loadDocuments,
    createCategory, updateCategory, deleteCategory,
  } = useFileLibraryStore();

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  const [editingCat, setEditingCat] = useState<FLCategory | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FLCategory | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('fa-folder');
  const [color, setColor] = useState('#64748B');
  const [sortOrder, setSortOrder] = useState<number>(99);
  const [active, setActive] = useState('Yes');
  const [saving, setSaving] = useState(false);

  // Standard Tags state
  const [standardTags, setStandardTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [editingTagIdx, setEditingTagIdx] = useState<number | null>(null);
  const [editTagValue, setEditTagValue] = useState('');
  const [tagsLoading, setTagsLoading] = useState(false);

  const DEFAULT_TAGS = [
    'After-Action Report', 'Briefing', 'Checklist', 'Contact List', 'Damage Assessment',
    'Emergency Plan', 'Exercise', 'FEMA', 'Finance', 'Form', 'Grant', 'ICS',
    'Incident Report', 'Insurance', 'Legal', 'Logistics', 'Map', 'Media Release',
    'Meeting Minutes', 'Memo', 'MOU/MOA', 'Mutual Aid', 'NIMS', 'Operations',
    'Photo Documentation', 'Policy', 'Procedure', 'Public Notice', 'Recovery',
    'Reference', 'Resource List', 'Safety', 'SOP', 'Situation Report', 'Template', 'Training',
  ];

  const loadTags = useCallback(async () => {
    setTagsLoading(true);
    try {
      const r = await fetch(`${API}/api/config/fl-standard-tags`, { headers: H });
      if (r.ok) {
        const data = await r.json();
        if (Array.isArray(data) && data.length > 0) {
          setStandardTags(data);
        } else {
          // Seed with defaults
          await fetch(`${API}/api/config/fl-standard-tags`, {
            method: 'PUT', headers: H, body: JSON.stringify(DEFAULT_TAGS),
          });
          setStandardTags(DEFAULT_TAGS);
        }
      } else {
        // Not found — seed
        await fetch(`${API}/api/config/fl-standard-tags`, {
          method: 'PUT', headers: H, body: JSON.stringify(DEFAULT_TAGS),
        });
        setStandardTags(DEFAULT_TAGS);
      }
    } catch { setStandardTags([]); }
    finally { setTagsLoading(false); }
  }, []);

  const saveTags = async (tags: string[]) => {
    await fetch(`${API}/api/config/fl-standard-tags`, {
      method: 'PUT', headers: H, body: JSON.stringify(tags),
    });
    setStandardTags(tags);
  };

  const handleAddTag = async () => {
    const tag = newTag.trim();
    if (!tag || standardTags.includes(tag)) return;
    const updated = [...standardTags, tag].sort((a, b) => a.localeCompare(b));
    await saveTags(updated);
    setNewTag('');
    notifications.show({ message: `Tag "${tag}" added`, color: 'green' });
  };

  const handleDeleteTag = async (idx: number) => {
    const updated = standardTags.filter((_, i) => i !== idx);
    await saveTags(updated);
    notifications.show({ message: 'Tag removed', color: 'gray' });
  };

  const handleEditTag = async (idx: number) => {
    const tag = editTagValue.trim();
    if (!tag || (standardTags.includes(tag) && standardTags[idx] !== tag)) return;
    const updated = [...standardTags];
    updated[idx] = tag;
    updated.sort((a, b) => a.localeCompare(b));
    await saveTags(updated);
    setEditingTagIdx(null);
    setEditTagValue('');
    notifications.show({ message: 'Tag updated', color: 'green' });
  };

  useEffect(() => {
    loadCategories();
    loadDocuments();
    loadTags();
  }, [loadCategories, loadDocuments, loadTags]);

  const sorted = [...categories].sort((a, b) => (Number(a.sortOrder) || 99) - (Number(b.sortOrder) || 99));

  function getDocCount(catName: string): number {
    return documents.filter(d => d.category === catName).length;
  }

  function handleOpenAdd() {
    setEditingCat(null);
    setName('');
    setIcon('fa-folder');
    setColor('#64748B');
    setSortOrder(99);
    setActive('Yes');
    openModal();
  }

  function handleOpenEdit(cat: FLCategory) {
    setEditingCat(cat);
    setName(cat.name);
    setIcon(cat.icon || 'fa-folder');
    setColor(cat.color || '#64748B');
    setSortOrder(Number(cat.sortOrder) || 99);
    setActive(cat.active === 'true' || cat.active === 'Yes' ? 'Yes' : 'No');
    openModal();
  }

  async function handleSave() {
    if (!name.trim()) {
      notifications.show({ title: 'Required', message: 'Enter a category name.', color: 'red' });
      return;
    }
    setSaving(true);
    try {
      const data: Partial<FLCategory> = {
        name: name.trim(),
        icon,
        color,
        sortOrder: String(sortOrder),
        active: active === 'Yes' ? 'true' : 'false',
      };
      if (editingCat) {
        await updateCategory(editingCat.id, data);
        notifications.show({ title: 'Updated', message: 'Category updated.', color: 'green' });
      } else {
        await createCategory(data);
        notifications.show({ title: 'Created', message: 'Category created.', color: 'green' });
      }
      closeModal();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to save category.',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteCategory(deleteTarget.id);
      notifications.show({ title: 'Deleted', message: 'Category deleted.', color: 'green' });
      closeDelete();
      setDeleteTarget(null);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to delete category.',
        color: 'red',
      });
    }
  }

  if (loading) {
    return <Center py="xl"><Loader size="sm" /></Center>;
  }

  return (
    <div>
      <Title order={3} mb="md" c="black">Categories & Tags</Title>

      <Tabs defaultValue="categories" mb="md">
        <Tabs.List mb="md">
          <Tabs.Tab value="categories" leftSection={<IconCategory size={14} />}>Categories</Tabs.Tab>
          <Tabs.Tab value="tags" leftSection={<IconTag size={14} />}>Standard Tags</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="tags">
          <Paper p="md" withBorder radius="sm">
            <Group justify="space-between" mb="md">
              <Text fw={600} size="sm">Standard Tags</Text>
              <Text size="xs" c="dimmed">{standardTags.length} tags defined</Text>
            </Group>
            <Text size="xs" c="dimmed" mb="md">
              Standard tags are available as suggestions when tagging documents. Add, edit, or remove tags below.
            </Text>
            <Group gap="xs" mb="md">
              <TextInput
                placeholder="New tag name..."
                value={newTag}
                onChange={e => setNewTag(e.target.value)}
                size="xs"
                style={{ flex: 1 }}
                onKeyDown={e => { if (e.key === 'Enter') handleAddTag(); }}
              />
              <Button size="xs" leftSection={<IconPlus size={12} />} onClick={handleAddTag} disabled={!newTag.trim()}>
                Add Tag
              </Button>
            </Group>
            <Divider mb="sm" />
            {tagsLoading ? (
              <Center py="md"><Loader size="sm" /></Center>
            ) : standardTags.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">No standard tags yet. Add one above.</Text>
            ) : (
              <Stack gap={4}>
                {standardTags.map((tag, idx) => (
                  <Group key={idx} justify="space-between" px="xs" py={4}
                    style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', borderRadius: 4 }}>
                    {editingTagIdx === idx ? (
                      <Group gap="xs" style={{ flex: 1 }}>
                        <TextInput
                          value={editTagValue}
                          onChange={e => setEditTagValue(e.target.value)}
                          size="xs"
                          style={{ flex: 1 }}
                          onKeyDown={e => { if (e.key === 'Enter') handleEditTag(idx); if (e.key === 'Escape') setEditingTagIdx(null); }}
                          autoFocus
                        />
                        <Button size="compact-xs" onClick={() => handleEditTag(idx)}>Save</Button>
                        <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setEditingTagIdx(null)}>Cancel</Button>
                      </Group>
                    ) : (
                      <>
                        <Badge variant="light" size="sm">{tag}</Badge>
                        <Group gap={4}>
                          <ActionIcon size="xs" variant="subtle" color="gray"
                            onClick={() => { setEditingTagIdx(idx); setEditTagValue(tag); }}>
                            <IconPencil size={12} />
                          </ActionIcon>
                          <ActionIcon size="xs" variant="subtle" color="red" onClick={() => handleDeleteTag(idx)}>
                            <IconTrash size={12} />
                          </ActionIcon>
                        </Group>
                      </>
                    )}
                  </Group>
                ))}
              </Stack>
            )}
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="categories">
      <Group justify="flex-end" mb="sm">
        <Button leftSection={<IconPlus size={14} />} size="xs" onClick={handleOpenAdd}>
          Add Category
        </Button>
      </Group>

      <Paper radius="sm" withBorder style={{ overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--bg-secondary)', borderBottom: 'var(--border)' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Icon</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Color</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Documents</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Sort Order</th>
              <th style={{ padding: '8px 12px', textAlign: 'center' }}>Active</th>
              <th style={{ padding: '8px 12px' }}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
                  No categories yet. Add one to get started.
                </td>
              </tr>
            ) : sorted.map(cat => (
              <tr key={cat.id} style={{ borderBottom: '1px solid var(--frost)' }}>
                <td style={{ padding: '8px 12px', fontWeight: 500 }}>{cat.name}</td>
                <td style={{ padding: '8px 12px' }}>
                  <Text size="xs" c="dimmed" ff="monospace">{cat.icon || '—'}</Text>
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <Group gap="xs">
                    <div style={{
                      width: 16, height: 16, borderRadius: 3,
                      background: cat.color || '#64748B',
                      border: '1px solid rgba(0,0,0,0.1)',
                    }} />
                    <Text size="xs" c="dimmed" ff="monospace">{cat.color || '—'}</Text>
                  </Group>
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>{getDocCount(cat.name)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>{cat.sortOrder || '—'}</td>
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <Badge
                    size="xs"
                    color={cat.active === 'true' || cat.active === 'Yes' ? 'green' : 'gray'}
                  >
                    {cat.active === 'true' || cat.active === 'Yes' ? 'Yes' : 'No'}
                  </Badge>
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <Group gap={4} justify="flex-end">
                    <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => handleOpenEdit(cat)}>
                      <IconPencil size={12} />
                    </ActionIcon>
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="red"
                      onClick={() => { setDeleteTarget(cat); openDelete(); }}
                    >
                      <IconTrash size={12} />
                    </ActionIcon>
                  </Group>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Paper>
        </Tabs.Panel>
      </Tabs>

      {/* Add/Edit Modal */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={editingCat ? 'Edit Category' : 'Add Category'}
        size="sm"
      >
        <TextInput
          label="Name"
          placeholder="Category name"
          value={name}
          onChange={e => setName(e.target.value)}
          required
          mb="sm"
        />
        <TextInput
          label="Icon Class"
          placeholder="e.g. fa-folder, fa-file-pdf"
          value={icon}
          onChange={e => setIcon(e.target.value)}
          mb="sm"
        />
        <div style={{ marginBottom: 8 }}>
          <Text size="sm" fw={500} mb={4}>Color</Text>
          <Group gap="xs" align="center">
            <input
              type="color"
              value={color}
              onChange={e => setColor(e.target.value)}
              style={{ width: 36, height: 36, padding: 2, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}
            />
            <TextInput
              placeholder="#64748B"
              value={color}
              onChange={e => setColor(e.target.value)}
              size="xs"
              style={{ flex: 1 }}
              ff="monospace"
            />
          </Group>
        </div>
        <NumberInput
          label="Sort Order"
          value={sortOrder}
          onChange={v => setSortOrder(typeof v === 'number' ? v : 99)}
          min={0}
          mb="sm"
        />
        <Select
          label="Active"
          data={[
            { value: 'Yes', label: 'Yes' },
            { value: 'No', label: 'No' },
          ]}
          value={active}
          onChange={v => setActive(v ?? 'Yes')}
          mb="md"
        />
        <Group justify="flex-end">
          <Button variant="subtle" size="xs" onClick={closeModal}>Cancel</Button>
          <Button size="xs" onClick={handleSave} loading={saving}>
            {editingCat ? 'Update' : 'Create'}
          </Button>
        </Group>
      </Modal>

      {/* Delete Confirmation */}
      <Modal opened={deleteOpened} onClose={closeDelete} title="Delete Category" size="sm">
        <Text size="sm" mb="md">
          Delete "{deleteTarget?.name}"? Documents using this category will not be deleted,
          but their category reference will become orphaned.
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" size="xs" onClick={closeDelete}>Cancel</Button>
          <Button color="red" size="xs" onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>
    </div>
  );
}
